import { blipAiSuiteUrl } from 'app.constants';
// Constants
const BUCKET_NOT_FOUND_ERROR_CODE = 67;
const CLIENT_CREDENTIALS_BUCKET_KEY = 'blip_ai_suite:client_credentials';
export class AnswersService {
    static $inject = ['$http', 'BucketService', 'MessagingHubService', 'AI_ANSWERS_AUTH_HOST', 'AI_ANSWERS_BASE_URL'];
    constructor($http, BucketService, MessagingHubService, AI_ANSWERS_AUTH_HOST, AI_ANSWERS_BASE_URL) {
        'ngInject';
        this.$http = $http;
        this.BucketService = BucketService;
        this.MessagingHubService = MessagingHubService;
        this.AI_ANSWERS_AUTH_HOST = AI_ANSWERS_AUTH_HOST;
        this.AI_ANSWERS_BASE_URL = AI_ANSWERS_BASE_URL;
    }
    /*
    * Checks if the ai suite application is enabled.
    */
    async isEnabled() {
        try {
            await this.BucketService.get(CLIENT_CREDENTIALS_BUCKET_KEY);
            return true;
        }
        catch (e) {
            if (e.code == BUCKET_NOT_FOUND_ERROR_CODE) {
                return false;
            }
            throw new Error(`[AI Answers] Fail on get answers client credentials: ${e}`);
        }
    }
    async setAccessToken() {
        if (!this.accessToken) {
            this.accessToken = await this.getAccessToken();
        }
    }
    /**
     * Gets access token associated with the ai suite application.
     */
    async getAccessToken() {
        const CLIENT_CREDENTIALS_BUCKET_KEY = 'blip_ai_suite:client_credentials';
        try {
            const credentials = await this.getBucketCredentials(CLIENT_CREDENTIALS_BUCKET_KEY);
            const data = await this.getToken(credentials.client_id, credentials.client_secret);
            return data.access_token;
        }
        catch (e) {
            throw new Error(`[AI Answers] Fail on get access token from answers: ${e}`);
        }
    }
    /**
     * Gets the credentials from the bucket.
     */
    async getBucketCredentials(bucketKey) {
        try {
            const credentials = await this.MessagingHubService.sendCommand({
                method: 'get',
                uri: `/buckets/${bucketKey}`,
            });
            return credentials;
        }
        catch (e) {
            throw new Error(`[AI Answers] Fail on get bucket credentials: ${e}`);
        }
    }
    /**
     * Gets the access token from the token endpoint.
     */
    async getToken(clientId, clientSecret) {
        try {
            const { data } = await this.$http.post(`${blipAiSuiteUrl}/prod/token`, {
                grant_type: 'client_credentials',
                client_id: clientId,
                client_secret: clientSecret,
            }, {
                headers: {
                    Authorization: undefined,
                },
            });
            return data;
        }
        catch (e) {
            throw new Error(`[AI Answers] Fail on get token: ${e}`);
        }
    }
    /**
     * List of available assistants in the answers platform.
     */
    async getAvailableAssistants() {
        try {
            const { data } = await this.$http.get(`${this.AI_ANSWERS_BASE_URL}/assistant`, {
                headers: {
                    accept: 'application/json',
                    'Content-Type': 'application/json',
                    Authorization: this.accessToken,
                },
            });
            return await data.map((assistant) => ({
                tenantId: assistant.tenant_id,
                assistantId: assistant.assistant_id,
                assistantName: assistant.assistant_name,
                configs: assistant.configs,
                createdAt: assistant.created_at,
                updatedAt: assistant.updated_at,
            }));
        }
        catch (e) {
            throw new Error(`[AI Answers] Fail on get available assistants: ${e}`);
        }
    }
    async getPublishedAssistants() {
        return (await this.getAvailableAssistants()).filter((assistant) => assistant.configs.PUBLISHED);
    }
    /**
     * Gets the assistant configuration.
     */
    async getAssistantConfigurations(assistantId) {
        try {
            const { data } = await this.$http.get(`${this.AI_ANSWERS_BASE_URL}/assistant/${assistantId}/configs`, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: this.accessToken,
                },
            });
            return await data.map((assistant) => ({
                configurationId: assistant.config_id,
                assistantName: assistant.assistant_name,
                assistantId: assistant.assistant_id,
                tenantId: assistant.tenant_id,
                companyName: assistant.company_name,
                agentType: assistant.agent_type,
                profile: assistant.profile,
                invalidAnswer: assistant.invalid_answer,
                guidelines: assistant.guidelines,
                redirectGuidelines: assistant.redirect_guidelines,
                createdAt: assistant.created_at,
                updatedAt: assistant.updated_at,
            }));
        }
        catch (e) {
            throw new Error(`[AI Answers] Fail on get assistant configuration: ${e}`);
        }
    }
    async fetchConfiguration() {
        try {
            await this.setAccessToken();
            const availableAssistants = await this.getAvailableAssistants();
            if (availableAssistants.length > 0) {
                const assistantsConfiguration = await this.getAssistantConfigurations(availableAssistants[0].assistantId);
                if (assistantsConfiguration) {
                    return {
                        ...assistantsConfiguration[0],
                        assistantName: availableAssistants[0].assistantName,
                    };
                }
            }
        }
        catch (e) {
            throw new Error(`[AI Answers] Fail on fetch assistant configuration: ${e}`);
        }
    }
    /**
     * Creates a new assistant in the answers platform.
     */
    async createAssistant(credentials, assistant, configuration) {
        const result = await this.getToken(credentials.client_id, credentials.client_secret);
        this.accessToken = result.access_token;
        return await this.createNewAssistant(assistant, configuration);
    }
    /**
     * Creates a new assistant in the answers platform.
     */
    async createNewAssistant(assistant, configuration) {
        try {
            const { data } = await this.$http.post(`${this.AI_ANSWERS_BASE_URL}/assistant`, {
                assistant_name: assistant.assistantName,
                assistant_config: {
                    company_name: configuration.companyName,
                    profile: configuration.profile,
                    agent_type: 'faq',
                    invalid_answer: configuration.invalidAnswer,
                    guidelines: configuration.guidelines,
                },
            }, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: this.accessToken,
                },
            });
            return {
                tenantId: data.tenant_id,
                assistantId: data.assistant_id,
                assistantName: data.assistant_name,
                configs: data.configs,
                createdAt: data.created_at,
                updatedAt: data.updated_at,
            };
        }
        catch (e) {
            throw new Error(`[AI Answers] Failed to create new assistant: ${e}`);
        }
    }
    /**
     * Edits the assistant configuration.
     */
    async editAssistantConfiguration(configuration) {
        try {
            const { data } = await this.$http.put(`${this.AI_ANSWERS_BASE_URL}/assistant/${configuration.assistantId}/config`, {
                company_name: configuration.companyName,
                profile: configuration.profile,
                agent_type: configuration.agentType,
                invalid_answer: configuration.invalidAnswer,
                redirect_guidelines: configuration.redirectGuidelines,
                guidelines: configuration.guidelines,
            }, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: this.accessToken,
                },
            });
            return {
                configurationId: data.config_id,
                assistantId: data.assistant_id,
                tenantId: data.tenant_id,
                companyName: data.company_name,
                agentType: data.agent_type,
                profile: data.profile,
                invalidAnswer: data.invalid_answer,
                guidelines: data.guidelines,
                redirectGuidelines: data.redirect_guidelines,
                createdAt: data.created_at,
                updatedAt: data.updated_at,
                assistantName: data.assistant_name,
            };
        }
        catch (e) {
            throw new Error(`[AI Answers] Fail on edit assistant configuration: ${e}`);
        }
    }
    /**
     * set registration
     */
    async setRegistration(tenantId, botId, blipAccessToken) {
        try {
            const { data } = await this.$http({
                method: 'POST',
                url: `${this.AI_ANSWERS_AUTH_HOST}/registration/tenant/${tenantId}/bot/${botId}/product/blip-answers`,
                headers: {
                    // AngularJS needs the 'Content-Type' to be undefined or null when sending FormData
                    'Content-Type': undefined,
                    Authorization: blipAccessToken,
                },
            });
            return data;
        }
        catch (e) {
            throw new Error(`[AI Answers] Fail on registration assistant: ${e}`);
        }
    }
    /**
     * Replace the Blip token with the currently used AI token..
     */
    async replaceAccessToken(tenantId, botId, blipAccessToken) {
        try {
            const { data } = await this.$http.post(`${this.AI_ANSWERS_AUTH_HOST}/token`, {
                grant_type: 'authorization_code',
                tenant_id: tenantId,
                bot_id: botId,
                code: blipAccessToken,
            }, {
                headers: {
                    Authorization: undefined,
                },
            });
            return data.access_token;
        }
        catch (e) {
            throw new Error(`[AI Answers] Fail on get access token from answers: ${e}`);
        }
    }
    /**
     * Generate bot credentials.
     */
    async generateBotCredentials(tenantId, botId, accessToken) {
        try {
            const { data } = await this.$http.post(`${this.AI_ANSWERS_AUTH_HOST}/${tenantId}/${botId}/application`, {}, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: accessToken,
                },
            });
            return data;
        }
        catch (e) {
            throw new Error(`[AI Answers] Fail on get access token from answers: ${e}`);
        }
    }
    /**
     * Publishes the assistant configuration.
     */
    async publishAssistant(configuration) {
        try {
            const { data } = await this.$http.put(`${this.AI_ANSWERS_BASE_URL}/assistant/${configuration.assistantId}/publish-config`, {}, {
                headers: {
                    accept: 'application/json',
                    Authorization: this.accessToken,
                },
            });
            return {
                tenantId: data.tenant_id,
                assistantId: data.assistant_id,
                assistantName: data.assistant_name,
                configs: data.configs,
                createdAt: data.created_at,
                updatedAt: data.updated_at,
            };
        }
        catch (e) {
            throw new Error(`[AI Answers] Fail on publish assistant configuration: ${e}`);
        }
    }
}
AnswersService.$inject = ['$http', 'BucketService', 'MessagingHubService', 'AI_ANSWERS_AUTH_HOST', 'AI_ANSWERS_BASE_URL'];
